/**
 * Touch controls for phones and tablets: a virtual stick on the left (x steers, y leans the body), and on the right
 * a throttle pedal (analog: slide up for more), front and rear brake buttons, a foot-out button (held), a stand toggle
 * and a reset. In the default scheme (`beginner`) the stick's y is the lean (and the trick key), the front button is
 * the one BRAKE, and the rear, foot and stand buttons hide (the assist does them). Shown on coarse-pointer devices or with ?touch=1, or from the first
 * touch on the page (a touch screen can appear after load). Pointer events, so
 * it also works with a mouse for testing.
 */
import { InputFrame } from '../sim/input';
import { rampLever } from './keyboard';

/**
 * Stick displacement (px, screen y down) to steer and body axes in [-1, 1]. Each axis is read on its own: steer from
 * the sideways throw, full lock at STEER_FULL of the radius; the body only past a wider dead zone, so a thumb that
 * drifts up or down while steering neither weakens the steer nor shifts the rider (owner 2026-10-06: steering on a
 * phone was too weak, a radial stick needed a full sideways throw for full lock).
 */
export const STEER_DEAD = 0.08, STEER_FULL = 0.6, BODY_DEAD = 0.35, BODY_FULL = 0.9;
const axis = (v: number, dead: number, full: number): number => Math.sign(v) * Math.min(1, Math.max(0, (Math.abs(v) - dead) / (full - dead)));
export function stickAxes(dx: number, dy: number, radius: number): { steer: number; body: number } {
  // Left steers left (+1, like A); up leans forward (+1, like E). (+0 keeps a zero axis from being -0.)
  return { steer: axis(-dx / radius, STEER_DEAD, STEER_FULL) + 0, body: axis(-dy / radius, BODY_DEAD, BODY_FULL) + 0 };
}

/** Centre of a floating stick of radius `r` for a touch at (x, y): the whole stick stays on a w x h screen, `pad` px from its edges. */
export function stickOrigin(x: number, y: number, r: number, w: number, h: number, pad = 6): { x: number; y: number } {
  const clamp = (v: number, size: number): number => Math.min(Math.max(v, r + pad), Math.max(r + pad, size - r - pad));
  return { x: clamp(x, w), y: clamp(y, h) };
}

/** Throttle pedal: any touch is a useful 60 %, full from half way up (the lower quarter was a weak quarter throttle). */
export const pedalThrottle = (fromBottom01: number): number => Math.max(0.6, Math.min(1, 0.6 + 0.8 * fromBottom01));

export class TouchInput {
  active = false;
  private steer = 0;
  private body = 0;
  private throttle = 0;
  private front = 0;
  private rear = 0;
  private frontLever = 0;
  private rearLever = 0;
  private foot = false;
  private standing = false;
  private root: HTMLElement | null = null;
  /** Beginner mode (see setBeginner). */
  beginner = false;
  private resetRequested = false;
  used = false;

  constructor(private readonly doc: Document, force = false) {
    if (force || matchMedia('(pointer: coarse)').matches) this.enable();
  }

  /** Builds and shows the controls. Idempotent: the game calls it again when a touch screen shows up after load. */
  enable(): void {
    if (this.active) return;
    this.active = true;
    const doc = this.doc;
    const css = doc.createElement('style');
    css.textContent = `
      .mx-touch{--tb:calc(max(5vmin,14px) + env(safe-area-inset-bottom));--tl:calc(4vmin + env(safe-area-inset-left));--tr:calc(4vmin + env(safe-area-inset-right));position:fixed;inset:0;pointer-events:none;z-index:10;user-select:none;-webkit-user-select:none;touch-action:none}
      .mx-touch *{pointer-events:auto;touch-action:none;position:absolute;box-sizing:border-box;font:600 13px system-ui,sans-serif;color:#fff;
        display:flex;align-items:center;justify-content:center;text-shadow:0 1px 2px #000a}
      .mx-stickzone{left:0;bottom:0;width:46%;height:70%;border-radius:0}
      .mx-touch .mx-stick{pointer-events:none}
      .mx-stick{left:var(--tl);bottom:var(--tb);width:34vmin;height:34vmin;max-width:190px;max-height:190px;border-radius:50%;
        background:radial-gradient(circle,#ffffff18 0,#ffffff10 60%,#ffffff30 61%,#ffffff08 70%);border:2px solid #ffffff40}
      .mx-knob{width:40%;height:40%;border-radius:50%;background:#ffffff60;border:2px solid #fff9;pointer-events:none;left:30%;top:30%}
      .mx-gas{right:var(--tr);bottom:var(--tb);width:24vmin;height:40vmin;max-width:130px;max-height:220px;border-radius:18px;background:#2fbf5a70;border:2px solid #b9ffcf90}
      .mx-brake{right:calc(var(--tr) + min(24vmin,130px) + 3vmin);bottom:var(--tb);width:21vmin;height:21vmin;max-width:108px;max-height:108px;border-radius:50%;background:#e0404070;border:2px solid #ffb4b490}
      .mx-rear{right:calc(var(--tr) + min(24vmin,130px) + 6vmin + min(21vmin,108px));bottom:var(--tb);width:16vmin;height:16vmin;max-width:84px;max-height:84px;border-radius:50%;background:#c0602870;border:2px solid #ffc8a090}
      .mx-foot{right:calc(var(--tr) + min(24vmin,130px) + 6vmin + min(21vmin,108px));bottom:calc(var(--tb) + min(16vmin,84px) + 3vmin);width:16vmin;height:11vmin;max-width:84px;max-height:56px;border-radius:12px;background:#8a5cd870;border:2px solid #d4c0ff90}
      .mx-foot.on{background:#8a5cd8d0}
      .mx-touch.beginner .mx-rear,.mx-touch.beginner .mx-foot,.mx-touch.beginner .mx-stand{display:none}
      .mx-stand{right:calc(var(--tr) + min(24vmin,130px) + 3vmin);bottom:calc(var(--tb) + min(21vmin,108px) + 3vmin);width:18vmin;height:11vmin;max-width:96px;max-height:56px;border-radius:12px;background:#3a6fd870;border:2px solid #b4ccff90}
      .mx-stand.on{background:#3a6fd8d0}
      .mx-reset{right:3vmin;top:3vmin;width:14vmin;height:9vmin;max-width:80px;max-height:44px;border-radius:10px;background:#0006;border:1px solid #fff6}
    `;
    doc.head.appendChild(css);
    const root = doc.createElement('div');
    root.className = 'mx-touch';
    const el = (cls: string, text = '') => { const e = doc.createElement('div'); e.className = cls; e.textContent = text; root.appendChild(e); return e; };
    const zone = el('mx-stickzone'), stick = el('mx-stick'), knob = doc.createElement('div');
    knob.className = 'mx-knob'; stick.appendChild(knob);
    const gas = el('mx-gas', 'GAS'), brake = el('mx-brake', 'FRONT'), rear = el('mx-rear', 'REAR'), foot = el('mx-foot', 'FOOT');
    const stand = el('mx-stand', 'STAND'), reset = el('mx-reset', 'RESET');
    doc.body.appendChild(root);
    this.root = root;

    const hold = (e: HTMLElement, down: (ev: PointerEvent) => void, move: (ev: PointerEvent) => void, up: () => void) => {
      e.addEventListener('pointerdown', (ev) => { ev.preventDefault(); e.setPointerCapture(ev.pointerId); this.used = true; down(ev); move(ev); });
      e.addEventListener('pointermove', (ev) => { if (e.hasPointerCapture(ev.pointerId)) move(ev); });
      const end = (ev: PointerEvent) => { if (e.hasPointerCapture(ev.pointerId)) e.releasePointerCapture(ev.pointerId); up(); };
      e.addEventListener('pointerup', end); e.addEventListener('pointercancel', end);
    };
    // The stick is floating: it appears where the thumb lands (kept whole on screen, away from the system's edge-swipe
    // zone, whose back gesture used to leave the game) and goes back to its resting place on release.
    let anchor: { x: number; y: number } | null = null; // where the thumb landed: the stick's zero
    const radius = (): number => stick.offsetWidth / 2;
    hold(zone, (ev) => {
      const r = radius();
      anchor = { x: ev.clientX, y: ev.clientY };
      const o = stickOrigin(ev.clientX, ev.clientY, r, innerWidth, innerHeight); // drawn whole on screen, but zero stays at the thumb
      Object.assign(stick.style, { left: `${o.x - r}px`, top: `${o.y - r}px`, bottom: 'auto' });
    }, (ev) => {
      if (!anchor) return;
      const r = radius();
      let dx = ev.clientX - anchor.x, dy = ev.clientY - anchor.y;
      const len = Math.hypot(dx, dy);
      if (len > r) { dx *= r / len; dy *= r / len; }
      const a = stickAxes(dx, dy, r);
      this.steer = a.steer; this.body = a.body;
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
    }, () => {
      anchor = null; this.steer = 0; this.body = 0; knob.style.transform = '';
      stick.style.left = stick.style.top = stick.style.bottom = '';
    });
    hold(gas, () => {}, (ev) => {
      const r = gas.getBoundingClientRect();
      this.throttle = pedalThrottle((r.bottom - ev.clientY) / r.height);
    }, () => { this.throttle = 0; });
    hold(brake, () => { this.front = 1; }, () => {}, () => { this.front = 0; });
    hold(rear, () => { this.rear = 1; }, () => {}, () => { this.rear = 0; });
    hold(foot, () => { this.foot = true; foot.classList.add('on'); }, () => {}, () => { this.foot = false; foot.classList.remove('on'); });
    // Held: flips the rider's own sit / stand choice while the thumb is on it (like Space).
    hold(stand, () => { this.standing = true; stand.classList.add('on'); }, () => {}, () => { this.standing = false; stand.classList.remove('on'); });
    hold(reset, () => { this.resetRequested = true; }, () => {}, () => {});
    root.classList.toggle('beginner', this.beginner);
  }

  takeReset(): boolean { const r = this.resetRequested; this.resetRequested = false; return r; }

  /** Beginner mode: the front button becomes the one brake, rear and foot buttons hide. */
  setBeginner(on: boolean): void {
    this.beginner = on;
    this.root?.classList.toggle('beginner', on);
  }

  /** Merges touch input over the keyboard frame (touch wins on any axis it is using). `dt`: the physics step. */
  merge(k: InputFrame, dt = 1 / 240): InputFrame {
    if (!this.active || !this.used) return k;
    // Brake buttons squeeze their lever like a hand (see rampLever), not a slam.
    this.frontLever = rampLever(this.frontLever, this.front > 0, dt);
    this.rearLever = rampLever(this.rearLever, this.rear > 0, dt);
    const common = {
      throttle: Math.max(k.throttle, this.throttle),
      steer: this.steer !== 0 ? this.steer : k.steer,
      body: this.body !== 0 ? this.body : k.body,
      standing: this.standing || k.standing,
      autoPosture: true,
    };
    if (this.beginner) return { ...common, standing: false, brake: Math.max(k.brake, this.frontLever), autoFoot: true, assist: true };
    return {
      ...common,
      brake: k.brake,
      frontBrake: Math.max(k.frontBrake ?? 0, this.frontLever),
      rearBrake: Math.max(k.rearBrake ?? 0, this.rearLever),
      foot: !!k.foot || this.foot,
      autoFoot: k.autoFoot,
    };
  }
}
